export interface KeybindConfig {
    // Keybinds can use either event.key (character) or event.code (physical position)
    key?: string; // Character produced (e.g., "=", "-", "s") - works across keyboard layouts
    code?: string | string[]; // Physical key position (fallback for special keys like F4)
    modifiers: {
        ctrl?: boolean;
        alt?: boolean;
        shift?: boolean;
    };
    description: string;
    action: string;
    modes?: string[];
    blockInInputFields?: boolean;
}

export const KEYBINDS: { [key: string]: KeybindConfig } = {
    // ==========================================
    // ALWAYS AVAILABLE SHORTCUTS
    // ==========================================

    SAVE: {
        key: 's', // Match character, works with Ctrl+S or Cmd+S
        modifiers: {ctrl: true},
        description: 'Save',
        action: 'clickSaveButton',
        blockInInputFields: false // Save should work everywhere
    },

    ZOOM_IN: {
        key: '=', // Match the "=" character, regardless of which key produces it
        modifiers: {alt: true},
        description: 'Zoom In',
        action: 'zoomIn'
    },

    ZOOM_OUT: {
        key: '-', // Match the "-" character, regardless of which key produces it
        modifiers: {alt: true},
        description: 'Zoom Out',
        action: 'zoomOut'
    },

    ZOOM_TO_FIT: {
        key: '0', // Match the "0" character
        modifiers: {alt: true},
        description: 'Zoom to Fit',
        action: 'zoomToFit'
    },

    UNDO: {
        key: 'z', // Match character 'z'
        modifiers: {ctrl: true},
        description: 'Undo',
        action: 'undo',
        blockInInputFields: true // Don't interfere with text field undo
    },

    REDO: {
        key: 'z', // Match character 'z' with shift
        modifiers: {ctrl: true, shift: true},
        description: 'Redo',
        action: 'redo',
        blockInInputFields: true // Don't interfere with text field redo
    },

    // ==========================================
    // MODE-SPECIFIC SHORTCUTS
    // ==========================================

    SEND_TO_CLEARING: {
        key: 'c', // Match character 'c'
        modifiers: {ctrl: true, shift: true},
        description: 'Send to Clearing',
        action: 'sendToClearing',
        modes: ['K', 'E'] // Available in Classification and Extraction modes
    },

    SEND_TO_IRRLAEUFER: {
        key: 'i', // Match character 'i'
        modifiers: {ctrl: true, shift: true},
        description: 'Send to Irrläufer (Wrong Business Area)',
        action: 'sendToIrrlaeufer',
        modes: ['K', 'E'] // Available in Classification and Extraction modes
    },

    EXIT_WITHOUT_SAVING: {
        code: 'F4', // Special key, use code
        modifiers: {},
        description: 'Exit Without Saving',
        action: 'exitWithoutSaving',
        modes: ['E'] // Extraction mode only
    },

    CLEAR_ALL_EXTRACTED_FIELDS: {
        key: 'd', // Match character 'd'
        modifiers: {ctrl: true, shift: true},
        description: 'Clear All Extracted Fields',
        action: 'deleteExtractedFields',
        modes: ['E'] // Extraction mode only
    },

    NEXT_FIELD_OR_COMPLETE: {
        code: 'Enter', // Special key, use code
        modifiers: {},
        description: 'Next Field',
        action: 'nextFieldOrCompleteCase',
        modes: ['E'], // Extraction mode only
        blockInInputFields: false // Special handling in code
    },

    DELETE_CASE: {
        key: '0', // Match character '0'
        modifiers: {ctrl: true, alt: true},
        description: 'Delete Case',
        action: 'deleteCase',
        modes: ['K'] // Classification mode only
    }
};

export function getKeybindDisplay(config: KeybindConfig): string {
    const parts: string[] = [];

    if (config.modifiers.ctrl) parts.push('Ctrl');
    if (config.modifiers.alt) parts.push('Alt');
    if (config.modifiers.shift) parts.push('Shift');

    // Use key if available (character-based), otherwise use code (physical key)
    let displayKey: string;

    if (config.key) {
        // Character-based: display the character as-is
        displayKey = config.key.toUpperCase();
    } else if (config.code) {
        // Physical key: convert code to display format
        const keyCode = Array.isArray(config.code) ? config.code[0] : config.code;
        displayKey = keyCode;

        if (displayKey.startsWith('Key')) {
            displayKey = displayKey.replace('Key', '');
        } else if (displayKey.startsWith('Digit')) {
            displayKey = displayKey.replace('Digit', '');
        }
    } else {
        displayKey = '?';
    }

    parts.push(displayKey);

    return parts.join('+');
}

export function getKeybindsForMode(mode: string | null): KeybindConfig[] {
    return Object.values(KEYBINDS).filter(config =>
        !config.modes || config.modes.length === 0 || (mode && config.modes.includes(mode))
    );
}
